import React, {useEffect, useRef, useState} from "react";
import "./ProductDetail.scss";
import {Chart as ChartJS, registerables} from "chart.js";
import {Container, Grid2 as Grid} from "@mui/material";
import {DateTime} from "luxon";
import {useDispatch} from "react-redux";
import {
    Product,
    Inventory,
    Brand,
    Category,
    MasterCategory,
    Strain,
    Vendor,
    Item,
    Transaction
} from "../../../../../../models/dutchie";
import {Model, Client, getCookie, isEmpty} from "weed-js";
import Loading from "../../../../../../components/elements/Loading";
import {Link, useParams} from "react-router-dom";
import {currencyFormatter, dateFormatter} from "../../../../../../utils";
import {enqueueSnackbar} from "../../../../../../redux/notifications/Action";
import {DutchieUtils} from "./utils";
import {getActiveTimeRange} from "./utils";
import {DUTCHIE} from "../../../../../../constants";


ChartJS.register(...registerables);


const ProductSummary = ({desiredProduct, selectedIntegrationAccounts}) => {
    const {
        organizationId: _organizationId,
        productId: _productId,
        integration: _integration
    } = useParams();

    const dispatch = useDispatch();

    const selectedOrganizationId = parseInt(_organizationId) || getCookie("organizationId");
    const selectedIntegration = _integration;
    const productId = desiredProduct?.id || parseInt(_productId);
    const [product, setProduct] = useState(null);

    const [brand, setBrand] = useState(null);
    const [category, setCategory] = useState(null);
    const [strain, setStrain] = useState(null);
    const [vendor, setVendor] = useState(null);
    const [masterCategory, setMasterCategory] = useState(null);
    const [inventory, setInventory] = useState(null);
    const [firstTransaction, setFirstTransaction] = useState(null);
    const [lastTransaction, setLastTransaction] = useState(null);

    const [isLoading, setIsLoading] = useState(true);

    const abortControllerRef = useRef(null);
    const client = new Client();

    useEffect(() => {
        if (isEmpty(productId) || isNaN(productId) || isEmpty(selectedIntegrationAccounts)) {
            return;
        }
        if (selectedIntegration !== DUTCHIE) {
            return;
        }
        abortControllerRef.current?.abort(); // cancel previous request
        abortControllerRef.current = new AbortController();

        setIsLoading(true);

        const dutchieAccountIds = selectedIntegrationAccounts.map(account => account.id);

        const common = {
            items__product_id: productId,
            dutchie_account_id__in: dutchieAccountIds.join(","),
            limit: 1
        };

        Promise.all([
            Product.fetchById(
                client, productId, abortControllerRef.current
            ),
            Inventory.fetch(
                {
                    client,
                    params: {
                        package__product_id: productId,
                        dutchie_account_id__in: dutchieAccountIds.join(","),
                        order_by: "-last_modified_datetime_utc",
                        limit: 1, // Fetch only the latest inventory item
                    },
                    paginate: false,
                    cache: true,
                    abortController: abortControllerRef.current
                }
            ),
            Transaction.fetch({
                client,
                params: {...common, order_by: "transaction_date"},
                paginate: false,
                cache: true,
                abortController: abortControllerRef.current
            }),
            Transaction.fetch({
                client,
                params: {...common, order_by: "-transaction_date"},
                paginate: false,
                cache: true,
                abortController: abortControllerRef.current
            })
        ]).then(([
            fetchedProduct,
            fetchedInventories,
            fetchedFirstTransaction,
            fetchedLastTransaction]) => {


            setProduct(Product.byId.get(productId));
            setInventory(fetchedInventories.find(inv => inv.product_id === productId) || null);
            setFirstTransaction(fetchedFirstTransaction[0] || null);
            setLastTransaction(fetchedLastTransaction[0] || null)

            let promises = [];
            let fetchedModels = [];
            if (isEmpty(fetchedProduct)) {
                console.error(">>>>>>>>>>>>>>>>>>>>>>>>>>    ProductSummary, no product found with id:", productId);
                setIsLoading(false);
                dispatch(enqueueSnackbar({
                    message: "Product not found.",
                    options: {variant: "error"}
                }));
                return;
            }
            for (const [model, pk, setter] of [
                [Brand, fetchedProduct.brand_id, setBrand],
                [Category, fetchedProduct.category_id, setCategory],
                [Strain, fetchedProduct.strain_id, setStrain],
                [Vendor, fetchedProduct.vendor_id, setVendor],
            ]) {
                if (isEmpty(pk) || pk < 0) continue; // Skip if primary key is invalid
                promises.push(model.fetchById(client, pk, abortControllerRef.current));
                fetchedModels.push([model, setter]);
            }

            Promise.all(promises).then(([...responses]) => {
                let i = 1;
                let fetchedCategory = {};
                for (const [model, setter] of fetchedModels) {
                    const fetchedData = responses[i];
                    if (isEmpty(fetchedData)) {
                        setter(null);
                    } else {
                        setter(new model(fetchedData));
                    }
                    if (model === Category) {
                        fetchedCategory = fetchedData; // Save category for later use
                    }
                    i++;
                }

                if (fetchedCategory?.master_category_id && fetchedCategory.master_category_id !== -1) {
                    MasterCategory.fetchById(client, fetchedCategory.master_category_id, abortControllerRef.current).then(
                        (fetchedMasterCategory) => {
                            setMasterCategory(fetchedMasterCategory);
                            setIsLoading(false);
                        }
                    ).catch(err => {
                        console.error(">>>>>>>>>>>>>>>>>>>>>>>>>>    Failed to fetch master category:", err);
                        if (err.code === "ERR_CANCELED") {
                            return;
                        }
                        setIsLoading(false);
                    });
                } else {
                    setIsLoading(false);
                }
            }).catch(err => {
                if (err.code === "ERR_CANCELED") {
                    return;
                }
                console.error(">>>>>>>>>>>>>>>>>>>>>>>>>>    ProductSummary", err);
                setIsLoading(false);
                dispatch(enqueueSnackbar({
                    message: "Failed to fetch product details.",
                    options: {variant: "error"}
                }));
            });
        }).catch(err => {
            if (err.code === "ERR_CANCELED") {
                return;
            }
            console.error(">>>>>>>>>>>>>>>>>>>>>>>>>>    ProductSummary", err);
            setIsLoading(false);
        });

        return () => {
            abortControllerRef.current?.abort(); // abort on unmount or dependency change
        };
    }, [productId, selectedIntegrationAccounts]);

    if (isLoading) {
        return <div className="product-summary" style={{width: "100%", margin: 0, maxWidth: "100%"}}>
            <div style={{width: "100%", padding: "20px", boxSizing: "border-box", display: "flex", height: "500px", flexDirection: "row", alignItems: "center"}}>
                <Loading fullscreen={false} title={"Loading Product Summary"}/>
            </div>
        </div>;
    }

    if (isEmpty(product) && isEmpty(inventory.product) && isEmpty(inventory)) {
        return <div className="product-summary" style={{width: "100%", margin: 0, maxWidth: "100%"}}>
            <p>
                No product inventory data available. Inventory is only available for products since the later of 6/24/2025 or your inception date.
            </p>
        </div>;
    }

    const expires = inventory?.expiration_datetime && !inventory?.deleted ? DateTime.fromISO(inventory.expiration_datetime, {zone: "America/New_York"}).toLocaleString() : "N/A"

    return <div className="product-summary">
        <div>
            <img src={inventory ? inventory.image_url : product.image_url} alt={product.name} style={{minHeight: "100%", maxWidth: "100%", maxHeight: "400px"}}/>
        </div>
        <div>
            <h3>{DutchieUtils.getProductName({
                product,
                brand,
                vendor,
                masterCategory,
                category,
                strain,
                selectedOrganizationId,
                selectedIntegration
            })}</h3>
            <table className={"product-summary-table"}>
                <thead>
                    <tr>
                        <th>Price</th>
                        <th>Cost</th>
                        <th>First Purchased</th>
                        <th>Most Recently Purchased</th>
                        <th>Expires</th>
                        <th>Units on Hand</th>
                        <th>Strain</th>
                        <th>Vendor</th>
                        <th>Brand</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <p>{inventory?.unit_price ? currencyFormatter.format(inventory.unit_price) : `No price data available at the product level for this product. You can enter it into ${product.namespace} for better analytics.`}</p>
                        </td>
                        <td>
                            <p>{inventory?.unit_cost ? currencyFormatter.format(inventory.unit_cost) : `No unit cost data available at the product level. Please enter it into ${product.namespace} for better analytics`}</p>
                        </td>
                        <td>
                            <p>{firstTransaction ? DateTime.fromISO(firstTransaction.transaction_date, {zone: "America/New_York"}).toLocaleString() : "No transactions found"}</p>
                        </td>
                        <td>
                            <p>{lastTransaction ? DateTime.fromISO(lastTransaction.transaction_date, {zone: "America/New_York"}).toLocaleString() : "No transactions found"}</p>
                        </td>
                        <td>
                            {expires}
                        </td>
                        <td>
                            {inventory?.deleted ? 0 : inventory?.quantity_available}
                        </td>
                        <td>
                            {product?.strain?.name ? product.strain.name : "---"}
                        </td>
                        <td>
                            {product?.vendor?.name ? product.vendor.name : "---"}
                        </td>
                        <td>
                            {product?.brand?.name ? product.brand.name : "---"}
                        </td>
                    </tr>

                </tbody>
            </table>
            <p>{inventory?.description}</p>
        </div>
    </div>;
};



export default ProductSummary;
